import { FormEvent, ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signUp } from "aws-amplify/auth";
import {
    AuthError,
    AuthFooterLink,
    AuthHeading,
    BrandButton,
    BrandInput,
    GoogleButton,
    OrDivider,
} from "../components/BrandControls.tsx";
import {
    AUTH_ROUTES,
    authErrorMessage,
    continueWithGoogle,
    signUpAutoSignIn,
    signUpNeedsPassword,
} from "../authHelpers.ts";
import {
    nameFromEmail,
    validateEmail,
    validatePassword,
} from "../validation.ts";
import {
    continueWithSingleSignOn,
    discoverSingleSignOn,
} from "../ssoDiscovery.ts";
import { setCookie } from "@/util/cookie.ts";
import { rememberGaSessionId } from "@/app/auth/gaSession.ts";

const TOS_URL = `https://www.${import.meta.env.VITE_DOMAIN}/tos`;

export default function SignUpScreen({
    title = "Create an account",
    subtitle = "No password needed. We’ll email you a code.",
    afterHeading,
    askName = true,
}: {
    /** For a sign-up that starts somewhere of its own, such as an audit link. */
    title?: string;
    subtitle?: string;
    /** Between the heading and the form: what the sign-up is for. */
    afterHeading?: ReactNode;
    /**
     * Whether to ask for a name. Cognito requires one, so a sign-up that
     * does not ask takes it from the address (`nameFromEmail`); it can be
     * changed in the profile afterwards.
     */
    askName?: boolean;
}) {
    const navigate = useNavigate();
    const isUtmScout = useLocation().pathname === AUTH_ROUTES.signUpUtmScout;
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [errors, setErrors] = useState<Record<string, string | undefined>>(
        {},
    );
    const [formError, setFormError] = useState<string>();
    const [loading, setLoading] = useState(false);

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const nextErrors = {
            name: !askName || name.trim() ? undefined : "Name is required.",
            email: validateEmail(email),
            password: signUpNeedsPassword
                ? validatePassword(password)
                : undefined,
            agreed: agreed
                ? undefined
                : "You must agree to the Terms of Service.",
        };
        setErrors(nextErrors);
        if (Object.values(nextErrors).some(Boolean)) {
            return;
        }

        setLoading(true);
        setFormError(undefined);
        let leaving = false;
        try {
            // An address on a domain that signs in through single sign-on
            // gets no account of its own here: the browser goes to the
            // organization's identity provider, and whether that sign-in
            // reaches an account is management-api's to decide -- a member,
            // an invitee, or nobody (specs/saml-scim/identity-swap.md).
            // Before the attribution cookie: a sign-in through single sign-on
            // never starts a trial.
            const providerName = await discoverSingleSignOn(email);
            if (providerName) {
                leaving = true;
                await continueWithSingleSignOn(providerName);
                return;
            }

            setCookie("potentialSignUp", "true", 14);
            rememberGaSessionId();
            const { nextStep } = await signUp({
                username: email.trim(),
                password: signUpNeedsPassword ? password : undefined,
                options: {
                    userAttributes: {
                        name: askName ? name.trim() : nameFromEmail(email),
                        ...(isUtmScout ? { "custom:is_utm_scout": "1" } : {}),
                    },
                    autoSignIn: signUpAutoSignIn,
                },
            });

            // Always a code to confirm the address: the pre sign-up trigger
            // never auto-confirms an account.
            if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
                navigate(AUTH_ROUTES.confirmSignUp, {
                    state: { email: email.trim() },
                });
            }
        } catch (err) {
            // A redirect that failed to start never left: the button is free
            // to try again
            leaving = false;
            setFormError(authErrorMessage(err));
        } finally {
            if (!leaving) {
                setLoading(false);
            }
        }
    };

    return (
        <>
            <AuthHeading title={title} subtitle={subtitle} />
            {afterHeading}

            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                    <GoogleButton
                        onClick={() => continueWithGoogle(isUtmScout)}
                        disabled={loading}
                    />
                    <p className="text-center text-xs text-brand-grey">
                        By continuing, you agree to the{" "}
                        <a
                            href={TOS_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                        >
                            Terms of Service
                        </a>
                    </p>
                </div>

                <OrDivider />

                <form className="flex flex-col gap-8" onSubmit={onSubmit}>
                    <div className="flex flex-col gap-5">
                        <AuthError message={formError} />
                        <div className="flex flex-col gap-4">
                            {askName && (
                                <BrandInput
                                    label="Full name"
                                    autoComplete="name"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    error={errors.name}
                                    autoFocus
                                />
                            )}
                            <BrandInput
                                label="Email"
                                type="email"
                                autoComplete="email"
                                placeholder="What’s your work email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={errors.email}
                                autoFocus={!askName}
                            />
                            {signUpNeedsPassword && (
                                <BrandInput
                                    label="Password"
                                    revealable
                                    autoComplete="new-password"
                                    placeholder="Create a password"
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    error={errors.password}
                                />
                            )}
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="flex items-start gap-2.5 text-xs text-brand-grey-warm">
                                <input
                                    type="checkbox"
                                    checked={agreed}
                                    onChange={(e) =>
                                        setAgreed(e.target.checked)
                                    }
                                    className="mt-0.5 h-4 w-4 rounded border-brand-beige text-brand-pink focus:ring-brand-pink/40"
                                />
                                <span>
                                    I agree to the{" "}
                                    <a
                                        href={TOS_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline"
                                    >
                                        Terms of Service
                                    </a>
                                </span>
                            </label>
                            {errors.agreed && (
                                <p className="text-xs text-brand-pink">
                                    {errors.agreed}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <BrandButton type="submit" loading={loading}>
                            Create account
                        </BrandButton>
                        <AuthFooterLink
                            prompt="Already have an account?"
                            linkLabel="Sign in"
                            onClick={() => navigate(AUTH_ROUTES.signIn)}
                        />
                    </div>
                </form>
            </div>
        </>
    );
}
